/**
 * PlanFeatureRow — linha "ícone Check/X + label" usada em listas de
 * features de planos (Pricing) e bullets de comparação. Substitui o
 * padrão repetido ~30 vezes em Pricing.tsx + outras pages de billing.
 *
 * Uso:
 *   <PlanFeatureRow included>Tours ilimitados</PlanFeatureRow>
 *   <PlanFeatureRow included={false}>White Label</PlanFeatureRow>
 *   <PlanFeatureRow included emphasize>Suporte VIP</PlanFeatureRow>
 *
 * Variante de cor do check: por padrão usa accent (teal). Em planos
 * "highlighted" (mais populares) você pode passar `tone="primary"`.
 */
import * as React from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PlanFeatureRowProps {
  included: boolean;
  /** When true, label is bolder (used for emphasized features in plan cards). */
  emphasize?: boolean;
  /** Color of the Check icon when included. Defaults to "accent" (teal). */
  tone?: 'accent' | 'primary';
  className?: string;
  children: React.ReactNode;
}

export function PlanFeatureRow({
  included,
  emphasize,
  tone = 'accent',
  className,
  children,
}: PlanFeatureRowProps) {
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      {included ? (
        <Check
          className={cn(
            'w-4 h-4 shrink-0 mt-0.5',
            tone === 'primary' ? 'text-[var(--color-primary)]' : 'text-[var(--color-accent)]',
          )}
          strokeWidth={2.5}
        />
      ) : (
        <X className="w-4 h-4 shrink-0 mt-0.5 text-[var(--color-text-muted)] opacity-40" />
      )}
      <span
        className={cn(
          'text-[13px] leading-tight',
          included
            ? emphasize
              ? 'text-[var(--color-text)] font-semibold'
              : 'text-[var(--color-text)]'
            : 'text-[var(--color-text-muted)] line-through opacity-60',
        )}
      >
        {children}
      </span>
    </div>
  );
}
